"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var Types = require("../types");
var sdk_1 = require("@findify/sdk");
var immutable_1 = require("immutable");
var Cache_1 = require("./Cache");
var changes_1 = require("../utils/changes");
var format_1 = require("../utils/format");
var helpers_1 = require("../utils/helpers");
var deepMerge_1 = require("../utils/deepMerge");
var pickConfigProps = function (_a) {
    var debounce = _a.debounce, onError = _a.onError, _b = _a.immutable, immutable = _b === void 0 ? false : _b;
    return ({ debounce: debounce, onError: onError, immutable: immutable });
};
/**
 * Empty map of filters is a workaround for Smart Collections which have predefined filters,
 * that are lost on subsequent requests, because initially state is clear and it just skips generating
 * new state via queryToState
 */
var _initial = immutable_1.fromJS({ filters: {} });
/**
 * Agent is a stateful event-based interface to Findify SDK, providing it
 * with features like automated request cancellation, request throttling,
 * efficient updating of inner state & providing a simple API to express transitions
 * from one request to another (things like adding / removing filters, changing sorting, etc.)
 */
var Agent = /** @class */ (function () {
    function Agent(config) {
        var _this = this;
        this.type = Types.RequestType.Search;
        this._defaults = _initial;
        this.state = _initial;
        this.response = _initial;
        this.handlers = [];
        /**
         * Set specific query value
         * eq: .set('limit', 20)
         * eq: .set('filters', (prevFilters) => {...prevFilters, size: ['M']})
         * @param field [string] - query field
         * @param update [any|function] - function which will return new value or value
         */
        this.set = function (field, update) {
            var oldValue = _this.state.get(field);
            var fx = helpers_1.isFunction(update) && update;
            var value = fx ? immutable_1.fromJS(fx(_this.format(oldValue))) : immutable_1.fromJS(update);
            if (field !== 'offset')
                _this.reset('offset'); // Reset offset on query change
            if (fx && !value)
                return _this; // Skip new value setting if update doesn't returned new value
            var changes = changes_1.getChangedFields(oldValue, immutable_1.isImmutable(value) ? value : immutable_1.fromJS(value));
            if (changes === false)
                return _this;
            if (immutable_1.isImmutable(changes) ? !changes.isEmpty() : true) {
                _this.cache.set(field, changes);
            }
            else {
                _this.reset(field);
            }
            _this.fireEvent('set:' + field, changes, immutable_1.Map());
            return _this;
        };
        var request = this.request.bind(this);
        this.config = pickConfigProps(config);
        this.onError = config.onError && config.onError.bind(this);
        this.handleResponse = this.handleResponse.bind(this);
        this.provider = sdk_1.init(config);
        this.cache = new Cache_1.Cache(config.debounce
            ? helpers_1.debounce(request, config.debounce)
            : request);
    }
    /**
     * Set values witch will be added to request by default
     * The defaults could be overwrite by via .set function
     * @param defaults [{ q: string, filters: any[], sort: any[], limit: number, offset: number }]
     * @param noInitialRequest whether to try to resolve agent request by default
     */
    Agent.prototype.defaults = function (defaults, noInitialRequest) {
        if (noInitialRequest === void 0) { noInitialRequest = false; }
        this._defaults = deepMerge_1.default(this._defaults, immutable_1.fromJS(defaults));
        if (!noInitialRequest)
            this.cache.resolve();
        return this;
    };
    /**
     * Listen to changes in specific response field
     * eq: change:items
     * @param key [string]: [action]:[...[:fields]]
     * @param handler [function(state, meta)]: callback function
     */
    Agent.prototype.on = function (key, handler) {
        var _a = key.split(':'), event = _a[0], path = _a.slice(1);
        this.handlers.push({ handler: handler, key: key, path: path, event: event });
        return this;
    };
    /**
     * Listen to single change in specific response field
     * eq: change:items
     * @param key [string]: [action]:[...[:fields]]
     * @param handler [function(state, meta)]: callback function
     */
    Agent.prototype.once = function (key, handler) {
        var _this = this;
        var _handler = function () {
            var res = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                res[_i] = arguments[_i];
            }
            handler(res);
            _this.off(_handler);
        };
        return this.on(key, _handler);
    };
    /**
     * Remove change listeners
     * eq: .off(someCallback) - will remove listeners with callback function "someCallback"
     * eq: .off('change:items') - will remove all listeners who listen to changes in items
     * @param action [string]: [action]:[...[:fields]] | [function]
     */
    Agent.prototype.off = function (action) {
        if (!action)
            this.handlers = [];
        this.handlers = this.handlers.filter(function (_a) {
            var key = _a.key, handler = _a.handler;
            return (helpers_1.isFunction(action) ? handler : key) !== action;
        });
        return this;
    };
    /**
     * Reset query/or field to default value
     * @param field [string?]
     */
    Agent.prototype.reset = function (field) {
        if (!field) {
            this.state = _initial;
        }
        else {
            this.state = this.state.delete(field);
        }
        this.cache.reset(field);
        return this;
    };
    /**
     * Apply desired request state to agent
     * @param state next query parameters
     */
    Agent.prototype.applyState = function (state) {
        this.reset();
        for (var key in state)
            this.set(key, state[key]);
        if (state.offset)
            this.set('offset', state.offset);
    };
    Agent.prototype.emit = function (event, data) {
        var handlers = this.handlers.filter(function (_a) {
            var key = _a.key;
            return key === event;
        });
        if (!handlers)
            return;
        for (var index = 0; index < handlers.length; index++) {
            if (!handlers[index])
                continue;
            handlers[index].handler(data);
        }
    };
    Agent.prototype.fireEvent = function (event, changes, meta) {
        var handlers = this.handlers.filter(function (_a) {
            var key = _a.key;
            return key === event;
        });
        if (!handlers)
            return;
        for (var index = 0; index < handlers.length; index++) {
            if (!handlers[index])
                continue;
            handlers[index].handler(this.format(changes), this.format(meta));
        }
    };
    Agent.prototype.handleChanges = function (next, meta) {
        var prev = this.response;
        for (var index = 0; index < this.handlers.length; index++) {
            if (!this.handlers[index])
                return;
            var _a = this.handlers[index], path = _a.path, handler = _a.handler, event = _a.event;
            if (event !== 'change')
                continue;
            var update = next.getIn(path);
            var old = prev.getIn(path);
            if (update && (!old || !old.equals(update))) {
                handler(this.format(update), this.format(meta));
            }
        }
    };
    Agent.prototype.handleResponse = function (res) {
        var response = immutable_1.fromJS(res);
        var newState = format_1.queryToState(this.state, response.get('meta'), this._defaults);
        this.handleChanges(response, response.get('meta'));
        this.fireEvent('change:query', newState, response.get('meta'));
        this.state = newState;
        this.response = response;
    };
    /**
     * Creates map of parameters to pass to SDK, depending on agent type & current request state
     * @param cache Cache instance to extract request state from
     */
    Agent.prototype.createRequestBody = function (cache) {
        this.state = deepMerge_1.default(this.state, cache);
        var merge = this._defaults.mergeDeep(this.state);
        var params = format_1.stateToQuery(merge).toJS();
        var type = this.type;
        return { params: params, type: type };
    };
    /**
     * This function will fire after next tick after last .set or .default call,
     * or, if debounce option is specified in config, after debouncer calls it
     * @param cache [{any}] - established values
     */
    Agent.prototype.request = function (cache) {
        var _this = this;
        var params = this.createRequestBody(cache);
        return this.provider
            .send(params)
            .then(this.handleResponse)
            .catch(function (error) {
            _this.emit('error', error);
            return _this.onError
                ? _this.onError(error)
                : console.warn(error);
        });
    };
    /**
     * Converts value to pure JS structure
     * @param value
     */
    Agent.prototype.format = function (value) {
        return this.config.immutable
            ? value
            : immutable_1.isImmutable(value) ? value.toJS() : value;
    };
    return Agent;
}());
exports.Agent = Agent;
//# sourceMappingURL=Agent.js.map